import React, { useState, useEffect } from 'react';
import { useProfileModal } from '../../context/ProfileModalContext';
import { useLocale } from '../../context/LocaleContext';
import { Meeting, MeetingMemberPreferences, NameListMode } from '../../services/meetingService';
import { formatWorkshopStartSchedule } from '../../services/workshop2026Service';
import styles from '../../styles/workshop2026Page.module.css';

interface WorkshopWorkshopPreferencesProps {
  meeting: Meeting | null;
  preferences: MeetingMemberPreferences;
  profilePhone?: string;
  userEmail?: string;
  onSave: (updates: Partial<MeetingMemberPreferences>) => Promise<void>;
  onSavePhone: (phone: string) => Promise<void>;
  embedded?: boolean;
}

const WorkshopWorkshopPreferences: React.FC<WorkshopWorkshopPreferencesProps> = ({
  meeting,
  preferences,
  profilePhone,
  userEmail,
  onSave,
  onSavePhone,
  embedded = false,
}) => {
  const { t } = useLocale();
  const { openProfileModal } = useProfileModal();
  const [nameListMode, setNameListMode] = useState<NameListMode>(preferences.name_list_mode);
  const [includePhoneOnList, setIncludePhoneOnList] = useState(preferences.include_on_printed_phone_list);
  const [includeEmailOnList, setIncludeEmailOnList] = useState(preferences.include_email_on_list);
  const [sponsorWillingness, setSponsorWillingness] = useState(preferences.sponsor_willingness);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState(profilePhone || '');
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneSaved, setPhoneSaved] = useState(false);

  const hasPhone = !!profilePhone?.trim();
  const accountEmail = userEmail?.trim() || '';
  const startSchedule = formatWorkshopStartSchedule(meeting);

  useEffect(() => {
    setNameListMode(preferences.name_list_mode);
    setIncludePhoneOnList(preferences.include_on_printed_phone_list);
    setIncludeEmailOnList(preferences.include_email_on_list);
    setSponsorWillingness(preferences.sponsor_willingness);
  }, [preferences]);

  useEffect(() => {
    setPhoneDraft(profilePhone || '');
    if (profilePhone?.trim()) setEditingPhone(false);
  }, [profilePhone]);

  const persist = async (updates: Partial<MeetingMemberPreferences>) => {
    setSaving(true);
    setSaved(false);
    try {
      await onSave(updates);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePhone = async () => {
    const trimmed = phoneDraft.trim();
    if (!trimmed) return;
    setPhoneSaving(true);
    setPhoneSaved(false);
    try {
      await onSavePhone(trimmed);
      setEditingPhone(false);
      setPhoneSaved(true);
      setTimeout(() => setPhoneSaved(false), 2500);
    } finally {
      setPhoneSaving(false);
    }
  };

  const formContent = (
    <div className={styles.prefFormStack}>
      <div className={styles.giveToGetCallout}>
        <p className={styles.giveToGetTitle}>{t('workshop.2026.preferences.give_to_get_title')}</p>
        <p className={styles.giveToGetIntro}>{t('workshop.2026.preferences.give_to_get_lead')}</p>
        <ul className={styles.giveToGetList}>
          <li>{t('workshop.2026.preferences.give_names')}</li>
          <li>{t('workshop.2026.preferences.give_phones')}</li>
          <li>{t('workshop.2026.preferences.give_emails')}</li>
        </ul>
        <p className={styles.giveToGetNote}>{t('workshop.2026.preferences.email_login_note')}</p>
      </div>

      <div className={styles.prefGroup}>
        <h4 className={styles.prefGroupTitle}>{t('workshop.2026.preferences.name_heading')}</h4>
        <p className={styles.prefGroupDesc}>{t('workshop.2026.preferences.name_help')}</p>
        <div className={styles.prefChoiceStack}>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="nameList"
              checked={nameListMode === 'hidden'}
              onChange={() => {
                setNameListMode('hidden');
                persist({ name_list_mode: 'hidden' });
              }}
              disabled={saving}
            />
            <span>{t('workshop.2026.preferences.name_never')}</span>
          </label>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="nameList"
              checked={nameListMode === 'when_workshop_starts'}
              onChange={() => {
                setNameListMode('when_workshop_starts');
                persist({ name_list_mode: 'when_workshop_starts' });
              }}
              disabled={saving}
            />
            <span>{t('workshop.2026.preferences.name_at_start', { startSchedule })}</span>
          </label>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="nameList"
              checked={nameListMode === 'now'}
              onChange={() => {
                setNameListMode('now');
                persist({ name_list_mode: 'now' });
              }}
              disabled={saving}
            />
            <span>{t('workshop.2026.preferences.name_now')}</span>
          </label>
        </div>
      </div>

      <div className={styles.prefGroup}>
        <h4 className={styles.prefGroupTitle}>{t('workshop.2026.preferences.phone_heading')}</h4>
        <p className={styles.prefGroupDesc}>
          {t('workshop.2026.preferences.phone_help', { startSchedule })}
        </p>
        <div className={styles.prefChoiceStack}>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="phoneList"
              checked={includePhoneOnList}
              onChange={() => {
                setIncludePhoneOnList(true);
                persist({ include_on_printed_phone_list: true });
              }}
              disabled={saving}
            />
            <span>{t('workshop.2026.preferences.phone_yes')}</span>
          </label>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="phoneList"
              checked={!includePhoneOnList}
              onChange={() => {
                setIncludePhoneOnList(false);
                setEditingPhone(false);
                persist({ include_on_printed_phone_list: false });
              }}
              disabled={saving}
            />
            <span>{t('workshop.2026.preferences.phone_no')}</span>
          </label>
        </div>

        {includePhoneOnList && (
          <div className={styles.prefSubBlock}>
            {hasPhone && !editingPhone ? (
              <>
                <p className={styles.prefSubValue}>
                  {t('workshop.2026.preferences.saved_number', { phone: profilePhone || '' })}
                </p>
                <div className={styles.prefActionRow}>
                  <button
                    type="button"
                    className={styles.textButton}
                    onClick={() => {
                      setPhoneDraft(profilePhone || '');
                      setEditingPhone(true);
                    }}
                  >
                    {t('workshop.2026.preferences.update_number')}
                  </button>
                  <button type="button" className={styles.textButton} onClick={() => openProfileModal()}>
                    {t('workshop.2026.preferences.view_profile')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <label className={styles.prefSubLabel} htmlFor="workshop-phone">
                  {t('workshop.2026.preferences.phone_label')}
                </label>
                <p className={styles.prefSubHint}>
                  {hasPhone
                    ? t('workshop.2026.preferences.phone_updates_profile')
                    : t('workshop.2026.preferences.phone_saved_note')}
                </p>
                <div className={styles.prefPhoneRow}>
                  <input
                    id="workshop-phone"
                    type="tel"
                    className={styles.prefPhoneInput}
                    value={phoneDraft}
                    onChange={(e) => setPhoneDraft(e.target.value)}
                    placeholder={t('workshop.2026.preferences.phone_placeholder')}
                    autoComplete="tel"
                    disabled={phoneSaving}
                  />
                  <button
                    type="button"
                    className={styles.prefPhoneSaveBtn}
                    onClick={handleSavePhone}
                    disabled={phoneSaving || !phoneDraft.trim()}
                  >
                    {phoneSaving
                      ? t('workshop.2026.preferences.saving')
                      : t('workshop.2026.preferences.save_number')}
                  </button>
                </div>
                <div className={styles.prefActionRow}>
                  {hasPhone && editingPhone && (
                    <button
                      type="button"
                      className={styles.textButton}
                      onClick={() => {
                        setPhoneDraft(profilePhone || '');
                        setEditingPhone(false);
                      }}
                    >
                      {t('workshop.2026.preferences.cancel')}
                    </button>
                  )}
                  <button type="button" className={styles.textButton} onClick={() => openProfileModal()}>
                    {t('workshop.2026.preferences.view_profile')}
                  </button>
                </div>
                {phoneSaved && (
                  <p className={styles.prefStatusSaved}>{t('workshop.2026.preferences.phone_saved')}</p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className={styles.prefGroup}>
        <h4 className={styles.prefGroupTitle}>{t('workshop.2026.preferences.email_heading')}</h4>
        <p className={styles.prefGroupDesc}>
          {accountEmail
            ? t('workshop.2026.preferences.email_help_with', { email: accountEmail })
            : t('workshop.2026.preferences.email_help')}
        </p>
        <div className={styles.prefChoiceStack}>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="emailList"
              checked={includeEmailOnList}
              onChange={() => {
                setIncludeEmailOnList(true);
                persist({ include_email_on_list: true });
              }}
              disabled={saving}
            />
            <span>{t('workshop.2026.preferences.email_yes')}</span>
          </label>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="emailList"
              checked={!includeEmailOnList}
              onChange={() => {
                setIncludeEmailOnList(false);
                persist({ include_email_on_list: false });
              }}
              disabled={saving}
            />
            <span>{t('workshop.2026.preferences.email_no')}</span>
          </label>
        </div>

        {includeEmailOnList && accountEmail && (
          <div className={styles.prefSubBlock}>
            <p className={styles.prefSubValue}>
              {t('workshop.2026.preferences.shared_from_account', { email: accountEmail })}
            </p>
            <p className={styles.prefSubHint}>{t('workshop.2026.preferences.email_login_used')}</p>
          </div>
        )}
      </div>

      <div className={styles.prefGroup}>
        <h4 className={styles.prefGroupTitle}>{t('workshop.2026.preferences.sponsor_heading')}</h4>
        <p className={styles.prefGroupDesc}>{t('workshop.2026.preferences.sponsor_help')}</p>
        <div className={styles.prefChoiceStack}>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="sponsorWilling"
              checked={sponsorWillingness === 'yes'}
              onChange={() => {
                setSponsorWillingness('yes');
                persist({ sponsor_willingness: 'yes' });
              }}
              disabled={saving}
            />
            <span>{t('workshop.2026.preferences.yes')}</span>
          </label>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="sponsorWilling"
              checked={sponsorWillingness === 'temporary'}
              onChange={() => {
                setSponsorWillingness('temporary');
                persist({ sponsor_willingness: 'temporary' });
              }}
              disabled={saving}
            />
            <span>{t('workshop.2026.preferences.yes_temporary')}</span>
          </label>
          <label className={styles.prefChoiceOption}>
            <input
              type="radio"
              name="sponsorWilling"
              checked={sponsorWillingness === 'no'}
              onChange={() => {
                setSponsorWillingness('no');
                persist({ sponsor_willingness: 'no' });
              }}
              disabled={saving}
            />
            <span>{t('workshop.2026.preferences.no')}</span>
          </label>
        </div>
      </div>

      {saving && <p className={styles.prefStatus}>{t('workshop.2026.preferences.saving')}</p>}
      {!saving && saved && (
        <p className={styles.prefStatusSaved}>{t('workshop.2026.preferences.saved')}</p>
      )}
    </div>
  );

  if (embedded) {
    return <div className={styles.preferencesEmbedded}>{formContent}</div>;
  }

  return (
    <section id="workshop-preferences" className={styles.preferencesSection}>
      <h3 className={styles.sectionSubheading}>{t('workshop.2026.preferences.card_title')}</h3>
      <p className={styles.helperText}>{t('workshop.2026.preferences.standalone_hint')}</p>
      {formContent}
    </section>
  );
};

export default WorkshopWorkshopPreferences;
